import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import StyledDealflowMonthlyBreakdownGraphCard
  from 'components/organisms/Cards/DealflowMonthlyBreakdownGraphCard/DealflowMonthlyBreakdownGraphCard.styles';
import DealflowMonthlyBarChart from 'components/organisms/Charts/DealflowMonthlyBarChart/DealflowMonthlyBarChart';
import constants from 'helpers/constants';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import {useClientStatisticDealflowMonthly} from 'services/client/statistic/statistic.utils';
import Box from 'components/atoms/Layout/Box/Box';
import {P} from 'components/atoms/Text/Typography/Typography';
import {useLinkNavigate} from 'helpers/hooks/links';
import utils from 'helpers/utils';
import SimpleLoader from 'components/molecules/Loaders/SimpleLoader/SimpleLoader';

const DealflowMonthlyBreakdownGraphCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'DealflowMonthlyBreakdownGraphCard');

  const tableProvider = useTable();
  const graphState = tableProvider.graphState;

  const [dealflow, setDealflow] = useState(null);

  const navigate = useLinkNavigate();

  const listState = useEffectItem(tableProvider.appliedListState());
  const year = graphState.graphSettings['monthlyBreakdown']?.filter?.year;
  const months = graphState.graphSettings['monthlyBreakdown']?.filter?.months ?? 12;

  const [dealflowBreakdown, isLoading] = useClientStatisticDealflowMonthly(
    year,
    months,
    listState.search,
    listState.filter
  );

  useEffect(() => {
    if (dealflowBreakdown) {
      setDealflow(dealflowBreakdown);
    }
  }, [dealflowBreakdown]);

  const fields = useMemo(() => {
    const initialOption = constants.data.periodMonths.find((opt) => opt.value === months);

    return [{
      name: 'months',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      placeholder: 'Select a period',
      initial: initialOption,
      FormFieldProps: {
        autoFocus: false,
        hiddenLabel: true,
        clearable: false,
        size: 'small'
      },
      options: constants.data.periodMonths
    }];
  }, [months]);

  const setGraphSettingsEvent = useEffectEvent(graphState.setGraphSettings);
  const handleFilterChange = useCallback((field, value) => {
    setGraphSettingsEvent?.((current) => ({
      ...current,
      'monthlyBreakdown': {
        ...current?.['monthlyBreakdown'],
        filter: {
          ...current?.['monthlyBreakdown']?.filter,
          months: value?.value ?? value
        }
      }
    }));
  }, [setGraphSettingsEvent]);

  const handleVisibilityChange = (visibility) => {
    graphState.setGraphSettings((current) => ({
      ...current,
      'monthlyBreakdown': {
        ...current?.['monthlyBreakdown'],
        visibility
      }
    }));
  };

  const handleClick = (e, release, data) => {
    const group = data.tooltipPayload?.[0]?.dataKey;
    let month = data.month;
    if (group && month) {
      month = new Date(month);
      navigate({
        event: e,
        to: `/dealflow/table?custom=dealflowMonthly:${group}_${month.getFullYear()}${(month.getMonth() + 1) < 10 ? '0' : ''}${month.getMonth() + 1}`,
        keepSearchParams: true
      });
    }
  };

  return <StyledDealflowMonthlyBreakdownGraphCard ref={ref} {...innerProps}
                                                  title="Monthly breakdown"
                                                  context={(fields ? <InlineForm onChange={handleFilterChange}
                                                                                 fields={fields} /> : null)}>
    {(utils.isDefined(dealflow) && dealflow.length > 0) ?
      <DealflowMonthlyBarChart dealflow={dealflow}
                               showLegend={true}
                               isLoading={isLoading}
                               onClick={handleClick}
                               visibility={graphState.graphSettings['monthlyBreakdown']?.visibility}
                               onVisibilityChange={handleVisibilityChange} /> : null}

    {(!utils.isDefined(dealflow) || dealflow.length === 0) ? (
      (!utils.isDefined(dealflow) || isLoading) ?
        <SimpleLoader className="GraphCard-loader" /> :
        <Box className="GraphCard-empty">
          <P>No deal flow data found</P>
        </Box>
    ) : null}
  </StyledDealflowMonthlyBreakdownGraphCard>
});

DealflowMonthlyBreakdownGraphCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default DealflowMonthlyBreakdownGraphCard;
